import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio,
  Stepper, Step, StepLabel, Tabs, Tab, MenuItem, Select, InputLabel,
  Alert, CircularProgress, Paper, Chip, IconButton, Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useAuth } from '../../auth/AuthContext';
import {
  listUsers, listAppContexts, getAccessProfile,
  createServicePrincipal, createScopedKey, createAccessProfile,
} from '../../api/partnerApi';
import ScopeEditor, { emptyClause, validateClauses } from './ScopeEditor';

// ---------------------------------------------------------------------------
// ScopedKeyCreateDialog — 5-step wizard for minting an ssk_* key.
//
// Steps (handoff doc §4 Step 4):
//   1. basics       — keyName + env (drives ssk_live_* vs ssk_test_* prefix)
//   2. bind         — pick a PartnerUser (Humans | Services tabs); SERVICE
//                     tab supports inline creation
//   3. context      — pick an AppContext + verify the AccessProfile exists
//                     for (context, user). Inline "+ Create profile" lands
//                     in Step 5 (disabled here for now).
//   4. review       — read-only summary
//   5. confirmation — call createScopedKey; show rawKey ONCE on 201, or a
//                     "key already exists" notice on 200 (idempotent match)
//
// Backend contract: createScopedKey accepts
//   { keyName, tenantId, contextId, userId }
// and is idempotent on that 4-tuple. The 5-min Rust authorizer cache means
// fresh keys take up to 5 minutes to start authorizing — surface that in
// the confirmation step where it matters.
// ---------------------------------------------------------------------------

const STEPS = ['basics', 'bind', 'context', 'review', 'confirmation'];
const STEP_LABELS = {
  basics: 'Basics',
  bind: 'Bind to user',
  context: 'App context',
  review: 'Review',
  confirmation: 'Confirmation',
};

/**
 * Validate keyName per backend rules — `PartnerAdminKeyHandler.validateKeyName`.
 * Returns the error message or null if valid.
 */
function validateKeyName(name) {
  if (!name || !name.trim()) return 'Key name is required';
  if (name !== name.trim()) return 'No leading or trailing whitespace';
  if (name.length > 100) return 'Must be 100 characters or fewer';
  return null;
}

// ---------------------------------------------------------------------------
// Top-level wizard component
// ---------------------------------------------------------------------------

export default function ScopedKeyCreateDialog({ open, onClose, initialEnv = 'live' }) {
  const { user } = useAuth();
  const qc = useQueryClient();

  const [step, setStep] = useState('basics');
  const [keyName, setKeyName] = useState('');
  const [env, setEnv] = useState(initialEnv);
  const [boundUser, setBoundUser] = useState(null);  // full UserResponse object
  const [contextId, setContextId] = useState('');
  const [profileExists, setProfileExists] = useState(false);  // populated by ContextStep
  const [result, setResult] = useState(null);  // { rawKey?, keyMeta }
  const [submitError, setSubmitError] = useState(null);

  // Reset all state when the dialog opens, in case it was closed mid-flow.
  useEffect(() => {
    if (open) {
      setStep('basics');
      setKeyName('');
      setEnv(initialEnv);
      setBoundUser(null);
      setContextId('');
      setProfileExists(false);
      setResult(null);
      setSubmitError(null);
    }
  }, [open, initialEnv]);

  const tenantId = env === 'live' ? user?.liveTenantId : user?.testTenantId;
  const nameError = validateKeyName(keyName);
  const stepIndex = STEPS.indexOf(step);
  const isReview = step === 'review';
  const isConfirmation = step === 'confirmation';

  const submitMutation = useMutation(
    () => createScopedKey(env, { keyName, tenantId, contextId, userId: boundUser.id }),
    {
      onSuccess: (data) => {
        // resultFromResponse guards a malformed / empty (e.g. 204) body — a
        // bare `data.rawKey` here would throw inside onSuccess and leave the
        // wizard stuck on the spinner (#98).
        setResult(resultFromResponse(data));
        setStep('confirmation');
        qc.invalidateQueries(['scopedKeys']);
      },
      onError: (err) => {
        setSubmitError(err.response?.data?.message || err.message || 'Create failed');
      },
    },
  );

  const canAdvance = (() => {
    switch (step) {
      case 'basics':  return !nameError && !!env;
      case 'bind':    return !!boundUser;
      case 'context': return !!contextId && profileExists;
      case 'review':  return !submitMutation.isLoading;
      default:        return false;
    }
  })();

  const handleNext = () => {
    if (isReview) {
      setSubmitError(null);
      submitMutation.mutate();
    } else {
      setStep(STEPS[stepIndex + 1]);
    }
  };

  const handleBack = () => {
    if (stepIndex > 0) setStep(STEPS[stepIndex - 1]);
  };

  const handleClose = () => {
    if (submitMutation.isLoading) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      // Don't unmount on close — preserves the result for a moment if the
      // user clicks outside the dialog. open=false hides it; reopening
      // resets via the useEffect above.
      keepMounted={false}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>Create scoped key</DialogTitle>
      <DialogContent>
        <Stepper activeStep={stepIndex} sx={{ mb: 4, mt: 1 }}>
          {STEPS.map((s) => (
            <Step key={s}>
              <StepLabel>{STEP_LABELS[s]}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 'basics' && (
          <BasicsStep
            keyName={keyName} setKeyName={setKeyName}
            env={env} setEnv={setEnv}
            nameError={nameError}
          />
        )}
        {step === 'bind' && (
          <BindStep
            env={env}
            boundUser={boundUser}
            onPickUser={setBoundUser}
          />
        )}
        {step === 'context' && (
          <ContextStep
            env={env}
            userId={boundUser?.id}
            contextId={contextId}
            setContextId={setContextId}
            onProfileResolved={setProfileExists}
          />
        )}
        {step === 'review' && (
          <ReviewStep
            keyName={keyName}
            env={env}
            tenantId={tenantId}
            user={boundUser}
            contextId={contextId}
          />
        )}
        {step === 'confirmation' && (
          <ConfirmationStep result={result} />
        )}

        {submitError && step !== 'confirmation' && (
          <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {isConfirmation ? (
          <Button
            onClick={handleClose}
            variant="contained"
            sx={{
              bgcolor: '#3B82F6',
              '&:hover': { bgcolor: '#2563EB' },
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Done
          </Button>
        ) : (
          <>
            <Button
              onClick={handleClose}
              disabled={submitMutation.isLoading}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBack}
              disabled={stepIndex === 0 || submitMutation.isLoading}
              sx={{ textTransform: 'none' }}
            >
              Back
            </Button>
            <Button
              onClick={handleNext}
              variant="contained"
              disabled={!canAdvance || submitMutation.isLoading}
              sx={{
                bgcolor: '#3B82F6',
                '&:hover': { bgcolor: '#2563EB' },
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {submitMutation.isLoading
                ? 'Creating…'
                : isReview ? 'Create' : 'Next'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Step 1: Basics — keyName + env
// ---------------------------------------------------------------------------

function BasicsStep({ keyName, setKeyName, env, setEnv, nameError }) {
  // Only show the validation error after the user has typed something —
  // an empty initial state shouldn't render as an error.
  const showError = !!nameError && keyName !== '';
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <TextField
        label="Key name"
        value={keyName}
        onChange={(e) => setKeyName(e.target.value)}
        placeholder="e.g. research-bot production"
        fullWidth
        autoFocus
        error={showError}
        helperText={showError ? nameError : `${keyName.length}/100 characters`}
      />
      <FormControl>
        <FormLabel>Environment</FormLabel>
        <RadioGroup row value={env} onChange={(e) => setEnv(e.target.value)}>
          <FormControlLabel value="live" control={<Radio />} label="Live" />
          <FormControlLabel value="test" control={<Radio />} label="Test" />
        </RadioGroup>
        <Typography variant="caption" color="text.secondary">
          The key will be prefixed <code>ssk_{env}_*</code> and scoped to your {env} tenant.
        </Typography>
      </FormControl>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Step 2: Bind to user — tabbed picker + inline service-principal create
// ---------------------------------------------------------------------------

function BindStep({ env, boundUser, onPickUser }) {
  const qc = useQueryClient();
  const [userTab, setUserTab] = useState(boundUser?.type === 'SERVICE' ? 'SERVICE' : 'HUMAN');
  const [createServiceOpen, setCreateServiceOpen] = useState(false);

  const { data: users, isLoading, error } = useQuery(
    ['users', env],
    () => listUsers(env),
  );

  // PartnerUserDB.type defaults to HUMAN for legacy rows (default-on-read in
  // UserResponse.from), so a user with no explicit type counts as HUMAN here.
  const filtered = (users || []).filter((u) => (u.type || 'HUMAN') === userTab);

  return (
    <Box>
      <Tabs
        value={userTab}
        onChange={(_, v) => setUserTab(v)}
        sx={{ mb: 2, '& .MuiTab-root': { textTransform: 'none' } }}
      >
        <Tab icon={<PersonIcon />} iconPosition="start" label="Humans" value="HUMAN" />
        <Tab icon={<SmartToyIcon />} iconPosition="start" label="Services" value="SERVICE" />
      </Tabs>

      {userTab === 'SERVICE' && (
        <Box sx={{ mb: 2 }}>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setCreateServiceOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            Create new service principal
          </Button>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.response?.data?.message || error.message || 'Failed to load users'}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : filtered.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ p: 3, textAlign: 'center' }}
        >
          No {userTab === 'HUMAN' ? 'humans' : 'service principals'} yet
          {userTab === 'SERVICE' && '. Click "Create new service principal" above.'}
        </Typography>
      ) : (
        <Box sx={{
          maxHeight: 300,
          overflow: 'auto',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}>
          {filtered.map((u) => (
            <Box
              key={u.id}
              onClick={() => onPickUser(u)}
              sx={{
                p: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
                bgcolor: boundUser?.id === u.id ? 'action.selected' : 'transparent',
                '&:last-of-type': { borderBottom: 'none' },
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontFamily: 'monospace', fontSize: 12, color: '#475569' }}
              >
                {u.id}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                {u.email && (
                  <Typography variant="caption" color="text.secondary">
                    {u.email}
                  </Typography>
                )}
                {u.externalId && (
                  <Typography variant="caption" color="text.secondary">
                    externalId: <code>{u.externalId}</code>
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <ServicePrincipalCreateDialog
        open={createServiceOpen}
        onClose={() => setCreateServiceOpen(false)}
        env={env}
        onCreated={(u) => {
          qc.invalidateQueries(['users', env]);
          setCreateServiceOpen(false);
          onPickUser(u);
        }}
      />
    </Box>
  );
}

function ServicePrincipalCreateDialog({ open, onClose, env, onCreated }) {
  const [externalId, setExternalId] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) { setExternalId(''); setError(null); }
  }, [open]);

  const mut = useMutation(
    () => createServicePrincipal(env, externalId.trim()),
    {
      onSuccess: (data) => onCreated(data),
      onError: (err) => setError(
        err.response?.data?.message || err.message || 'Create failed',
      ),
    },
  );

  const handleClose = () => {
    if (mut.isLoading) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Create service principal</DialogTitle>
      <DialogContent>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 2 }}
        >
          Service principals are PartnerUsers with <code>type=SERVICE</code> and no
          email — used for agents, CI bots, and any non-human identity.
        </Typography>
        <TextField
          label="External ID"
          autoFocus
          value={externalId}
          onChange={(e) => setExternalId(e.target.value)}
          placeholder="e.g. research-bot-prod"
          fullWidth
          helperText="Your system's identifier for this service. Used for idempotent lookups."
        />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={mut.isLoading}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => mut.mutate()}
          variant="contained"
          disabled={!externalId.trim() || mut.isLoading}
          sx={{
            bgcolor: '#3B82F6',
            '&:hover': { bgcolor: '#2563EB' },
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {mut.isLoading ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Step 3: App context — context picker + AccessProfile existence check
// ---------------------------------------------------------------------------

function ContextStep({ env, userId, contextId, setContextId, onProfileResolved }) {
  const qc = useQueryClient();
  const [createProfileOpen, setCreateProfileOpen] = useState(false);

  const { data: contexts, isLoading: contextsLoading, error: contextsError } = useQuery(
    ['appContexts', env],
    () => listAppContexts(env),
  );

  const {
    data: profile,
    isFetching: profileFetching,
    error: profileError,
  } = useQuery(
    ['accessProfile', env, contextId, userId],
    () => getAccessProfile(env, contextId, `usr_${userId}`),
    { enabled: !!contextId && !!userId },
  );

  // Bubble up to parent so canAdvance reflects the profile state.
  useEffect(() => {
    onProfileResolved(!!profile);
  }, [profile, onProfileResolved]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {contextsError && (
        <Alert severity="error">
          {contextsError.response?.data?.message || contextsError.message
           || 'Failed to load app contexts'}
        </Alert>
      )}
      <FormControl fullWidth>
        <InputLabel>App context</InputLabel>
        <Select
          value={contextId || ''}
          label="App context"
          onChange={(e) => setContextId(e.target.value)}
          disabled={contextsLoading}
        >
          {(contexts || []).map((c) => (
            <MenuItem key={c.contextId} value={c.contextId}>
              {c.contextId}{c.name ? ` — ${c.name}` : ''}
            </MenuItem>
          ))}
        </Select>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
          The app namespace within your tenant. Create one via
          {' '}<code>POST /v1/app-contexts</code> if your list is empty.
        </Typography>
      </FormControl>

      {contextId && (
        <Box>
          {profileFetching ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2">Checking AccessProfile…</Typography>
            </Box>
          ) : profileError ? (
            <Alert severity="error">
              {profileError.response?.data?.message || profileError.message}
            </Alert>
          ) : profile ? (
            <Alert severity="success" icon={false}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                ✓ AccessProfile exists for this (user, context) pair
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                Status: <strong>{profile.status}</strong>
                {profile.roleId && <> · Role: <strong>{profile.roleId}</strong></>}
                {profile.scopes && profile.scopes.length > 0 && (
                  <> · {profile.scopes.length} scope clause{profile.scopes.length === 1 ? '' : 's'}</>
                )}
              </Typography>
            </Alert>
          ) : (
            <Alert
              severity="warning"
              action={
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateProfileOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Create profile
                </Button>
              }
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                No AccessProfile yet
              </Typography>
              <Typography variant="caption" color="text.secondary">
                A key needs an AccessProfile to bind to. Click "Create profile"
                to define one inline, or use{' '}
                <code>POST /v1/app-contexts/{contextId}/profiles</code>.
              </Typography>
            </Alert>
          )}
        </Box>
      )}

      <InlineProfileCreateDialog
        open={createProfileOpen}
        onClose={() => setCreateProfileOpen(false)}
        env={env}
        contextId={contextId}
        userId={userId}
        onCreated={() => {
          setCreateProfileOpen(false);
          // Refetch the AccessProfile check so the green confirmation
          // replaces the warning in this step.
          qc.invalidateQueries(['accessProfile', env, contextId, userId]);
        }}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Inline AccessProfile creation sub-dialog
//
// Opens from the ContextStep's "Create profile" action when no profile
// exists for (contextId, userId). Embeds <ScopeEditor> for clause-by-
// clause permission building, then POSTs to /v1/app-contexts/{ctx}/profiles
// with the resolved scopes payload.
//
// The principalId is always `usr_<userId>` here — scoped keys bind to a
// PartnerUser, never to a key-as-principal in this wizard (key-as-
// principal is reserved for the deprecated Pattern B that MR #2 dropped).
// ---------------------------------------------------------------------------

function InlineProfileCreateDialog({ open, onClose, env, contextId, userId, onCreated }) {
  const [clauses, setClauses] = useState(() => [emptyClause()]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setClauses([emptyClause()]);
      setError(null);
    }
  }, [open]);

  const validationError = validateClauses(clauses);

  const mut = useMutation(
    () => createAccessProfile(env, contextId, {
      principalId: `usr_${userId}`,
      scopes: clauses,
      status: 'active',
    }),
    {
      onSuccess: () => onCreated(),
      onError: (err) => setError(
        err.response?.data?.message || err.message || 'Create failed',
      ),
    },
  );

  const handleClose = () => {
    if (mut.isLoading) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Create AccessProfile</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Binding <code>usr_{userId}</code> to context <code>{contextId}</code>.
          The profile defines which actions the scoped key (and any future
          tokens bound to this user-in-this-context) will be permitted.
        </Typography>
        <ScopeEditor value={clauses} onChange={setClauses} />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={mut.isLoading}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Tooltip title={validationError || ''}>
          <span>
            <Button
              onClick={() => { setError(null); mut.mutate(); }}
              variant="contained"
              disabled={!!validationError || mut.isLoading}
              sx={{
                bgcolor: '#3B82F6',
                '&:hover': { bgcolor: '#2563EB' },
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {mut.isLoading ? 'Creating…' : 'Create profile'}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Step 4: Review — read-only summary
// ---------------------------------------------------------------------------

function ReviewStep({ keyName, env, tenantId, user, contextId }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        Confirm the details below. After you click <strong>Create</strong> the
        raw key is shown <strong>once</strong> — copy it before closing.
      </Typography>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <ReviewRow label="Key name" value={keyName} />
        <ReviewRow label="Environment" value={`${env} (${tenantId})`} />
        <ReviewRow
          label="User"
          value={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                {user?.id}
              </Box>
              <Chip
                size="small"
                label={user?.type || 'HUMAN'}
                sx={{ fontSize: 10, height: 18, fontWeight: 600 }}
              />
            </Box>
          }
        />
        <ReviewRow label="App context" value={contextId} />
      </Paper>
    </Box>
  );
}

function ReviewRow({ label, value }) {
  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 2,
      py: 1,
      '&:not(:last-of-type)': { borderBottom: '1px solid', borderColor: 'divider' },
    }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Box sx={{ textAlign: 'right', fontWeight: 500, fontSize: 14 }}>{value}</Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Step 5: Confirmation — show rawKey ONCE or surface idempotent-match notice
// ---------------------------------------------------------------------------

/**
 * Normalize a createScopedKey response into the confirmation-step shape,
 * defensively. A null / non-object / empty (204) body must NOT throw — it
 * degrades to an idempotent-match-style `{ keyMeta: {} }` so the wizard always
 * reaches a sane confirmation screen instead of hanging (#98).
 *
 * @param {unknown} data
 * @returns {{ rawKey?: string, keyMeta: object }}
 */
export function resultFromResponse(data) {
  const keyMeta = data && typeof data === 'object' ? data : {};
  return keyMeta.rawKey ? { rawKey: keyMeta.rawKey, keyMeta } : { keyMeta };
}

export function ConfirmationStep({ result }) {
  const [copied, setCopied] = useState(false);
  if (!result) return null;

  // Idempotent match path — backend returns 200 with no rawKey when the
  // (partner, tenant, context, userId, keyName) tuple already maps to an
  // active key. Tell the user clearly that the existing key's raw value is
  // not recoverable.
  if (!result.rawKey) {
    const keyMeta = result.keyMeta || {};
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Key already exists
          </Typography>
          <Typography variant="caption">
            A key with this name + user + context tuple already exists. The raw
            key cannot be re-disclosed — if you've lost it, revoke this one from
            the Keys list and create a new one with a different name.
          </Typography>
        </Alert>
        {keyMeta.keyId && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <ReviewRow label="Existing key id" value={keyMeta.keyId} />
            {keyMeta.createdAt && <ReviewRow label="Created" value={keyMeta.createdAt} />}
          </Paper>
        )}
      </Box>
    );
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(result.rawKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box>
      <Alert severity="warning" sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Copy this key now — it won't be shown again
        </Typography>
      </Alert>
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: 'background.default',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            wordBreak: 'break-all',
            flexGrow: 1,
            fontSize: 13,
          }}
        >
          {result.rawKey}
        </Typography>
        <Tooltip title={copied ? 'Copied!' : 'Copy'}>
          <IconButton aria-label="Copy key" onClick={handleCopy}>
            <ContentCopyIcon />
          </IconButton>
        </Tooltip>
      </Paper>
      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          New keys take up to ~5 minutes to start authorizing requests (the Rust
          authorizer's cache window). Once that's elapsed you can test it with
          a partner-API call.
        </Typography>
      </Box>
    </Box>
  );
}
